class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this._ignoreScroll = false;
    this.onScroll = this.onScroll.bind(this);
    this.onPrev = () => this.goTo(this.index - 1);
    this.onNext = () => this.goTo(this.index + 1);
    this.onResize = () => this.refresh();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-upsell-viewport]');
    this.track = this.querySelector('[data-upsell-track]');
    this.slides = () => Array.from(this.querySelectorAll('[data-upsell-slide]'));
    this.prevBtn = this.querySelector('[data-upsell-prev]');
    this.nextBtn = this.querySelector('[data-upsell-next]');
    this.nav = this.querySelector('[data-upsell-nav]');
    this.drawer = this.closest('cart-drawer');

    this.addEventListener('click', this.onClick);
    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    this.viewport?.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.drawer) {
      this.drawerObserver = new MutationObserver(() => {
        if (this.drawer.classList.contains('active')) {
          requestAnimationFrame(() => this.refresh());
        }
      });
      this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    }

    this.resizeObserver = new ResizeObserver(() => this.refresh());
    if (this.viewport) this.resizeObserver.observe(this.viewport);

    this.refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('scroll', this.onScroll);
    this.drawerObserver?.disconnect();
    this.resizeObserver?.disconnect();
    clearTimeout(this._scrollEndTimer);
  }

  onClick(event) {
    const button = event.target.closest('[data-upsell-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    this.addToCart(button);
  }

  async addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const cart = document.querySelector('cart-drawer');
    button.disabled = true;
    button.classList.add('loading');
    button.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id).join(',')
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(button);
      }

      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const parsedState = await response.json();

      if (parsedState.status) {
        console.error(parsedState.description);
        return;
      }

      if (cart) {
        cart.classList.remove('is-empty');
        cart.renderContents(parsedState);
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          productVariantId: variantId,
          cartData: parsedState,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
      button.classList.remove('loading');
      button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    }
  }

  getMaxScrollLeft() {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  getScrollLeftForIndex(index) {
    const slides = this.slides();
    if (!slides.length) return 0;

    const clampedIndex = Math.max(0, Math.min(slides.length - 1, index));
    if (clampedIndex === slides.length - 1) {
      return this.getMaxScrollLeft();
    }

    return slides[clampedIndex].offsetLeft;
  }

  refresh() {
    const slides = this.slides();
    const hasMultiple = slides.length > 1;

    this.dataset.slideCount = slides.length;
    this.hidden = slides.length === 0;
    this.nav?.classList.toggle('cart-drawer-upsell__nav--hidden', !hasMultiple);
    if (!this.viewport || !slides.length) return;

    const viewportWidth = this.viewport.clientWidth;
    const ratioWidth = Math.round(viewportWidth * UPSELL_SLIDE_WIDTH_RATIO);
    const slideWidth = Math.min(hasMultiple ? ratioWidth : viewportWidth, UPSELL_SLIDE_MAX_WIDTH);

    slides.forEach((slide) => {
      slide.style.flex = `0 0 ${slideWidth}px`;
      slide.style.width = `${slideWidth}px`;
      slide.style.maxWidth = `${UPSELL_SLIDE_MAX_WIDTH}px`;
    });

    if (this.index >= slides.length) this.index = 0;
    if (hasMultiple) {
      this.goTo(this.index, false);
    } else {
      this.updateNav();
    }
  }

  goTo(index, smooth = true) {
    const slides = this.slides();
    if (!slides.length || !this.viewport) return;

    this.index = Math.max(0, Math.min(slides.length - 1, index));
    const targetLeft = this.getScrollLeftForIndex(this.index);

    this._ignoreScroll = true;
    this.viewport.scrollTo({
      left: targetLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });

    this.updateNav();

    clearTimeout(this._scrollEndTimer);
    this._scrollEndTimer = setTimeout(
      () => {
        this._ignoreScroll = false;
        this.syncIndexFromScroll();
      },
      smooth ? 400 : 0
    );
  }

  onScroll() {
    if (this._ignoreScroll) return;
    this.syncIndexFromScroll();
  }

  syncIndexFromScroll() {
    const slides = this.slides();
    if (!slides.length || !this.viewport) return;

    const scrollLeft = this.viewport.scrollLeft;
    const maxScroll = this.getMaxScrollLeft();

    if (maxScroll <= 1) {
      this.index = 0;
    } else if (scrollLeft <= 1) {
      this.index = 0;
    } else if (scrollLeft >= maxScroll - 1) {
      this.index = slides.length - 1;
    } else {
      let closestIndex = 0;
      let closestDistance = Infinity;

      slides.forEach((slide, i) => {
        const targetLeft = this.getScrollLeftForIndex(i);
        const distance = Math.abs(targetLeft - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      });

      this.index = closestIndex;
    }

    this.updateNav();
  }

  updateNav() {
    const slides = this.slides();
    if (!this.prevBtn || !this.nextBtn) return;

    this.prevBtn.disabled = this.index <= 0;
    this.nextBtn.disabled = this.index >= slides.length - 1;
  }
}

const UPSELL_SLIDE_MAX_WIDTH = 350;
const UPSELL_SLIDE_WIDTH_RATIO = 0.65;

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
